// THE PIECES `/outcome`, `/home` AND THE PROPERTY PAGE SHARE (WEB-PARITY-7).
//
// Every word is the app's (`components/afterFilingCopy.ts`), carried on its element as `data-app-copy`; every figure is
// the server's (`AppealSummary`, the snapshot). The icons are the app's SF Symbols for each result (Models.swift:65-74,
// OC-4), drawn inline here — no image is fetched — in the result's own tint (`OutcomeResult.tint`, StatusBadge.swift:65-76).

/* eslint-disable react-refresh/only-export-components --
   Small components and the two readers they use belong together, as `account/parts.tsx`'s do. */

import type { AppealSnapshot, AppealSummary, OutcomeResult } from "@contract";
import { afterTemplate, afterText, type AfterFilingId } from "../components/afterFilingCopy";
import { heldText, type AnyHeldId } from "../components/heldCopy";

/** One app line. The same as `HeldLine`, for an app string. */
export function AppSay({ id, as = "p", className }: {
  id: AfterFilingId;
  as?: "p" | "span" | "h1" | "h2" | "h3" | "b" | "em";
  className?: string;
}) {
  const Tag = as;
  return (
    <Tag className={className} data-app-copy={id}>
      {afterText(id)}
    </Tag>
  );
}

/** One app line with its `{name}` slots filled. */
export function AppSayTemplate({ id, values, as = "p", className }: {
  id: AfterFilingId;
  values: Readonly<Record<string, string | number>>;
  as?: "p" | "span" | "b" | "em" | "h1";
  className?: string;
}) {
  const Tag = as;
  return (
    <Tag className={className} data-app-copy={id}>
      {afterTemplate(id, values)}
    </Tag>
  );
}

/** The result's tint, as the app's `OutcomeResult.tint` (StatusBadge.swift:67-75). */
export function tintOf(result: OutcomeResult): "savings" | "primary" | "muted" | "danger" {
  switch (result) {
    case "won":
    case "partiallyWon":
      return "savings";
    case "filed":
      return "primary";
    case "denied":
    case "withdrawn":
      return "muted";
    case "raised":
      return "danger";
  }
}

/** The app's icon for each result (`OutcomeResult.icon`, Models.swift:65-74): paperplane, a seal with a check, a circle
 *  with a check, a seal with a cross, a circle with an up arrow, a circle with a turn-back arrow. Decorative: the row's
 *  words name the result. */
export function ResultIcon({ result }: { result: OutcomeResult }) {
  const seal = "M12 2.5l2.2 1.6 2.7-.2.9 2.6 2.3 1.4-.6 2.6 1.1 2.5-1.9 1.9-.3 2.7-2.6.7-1.6 2.2-2.6-.9-2.5 1-1.7-2.2-2.7-.6-.4-2.7-2-1.8 1-2.5-.7-2.6 2.3-1.5.8-2.6 2.7.1z";
  const circle = "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z";
  const body = (() => {
    switch (result) {
      case "filed":
        return <path d="M3 11.5L21 3l-6.5 18-3-7.5zM11.5 13.5L21 3" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />;
      case "won":
        return (
          <>
            <path d={seal} fill="currentColor" />
            <path d="M8.3 12.2l2.6 2.6 4.9-5.2" fill="none" stroke="var(--ink)" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
          </>
        );
      case "partiallyWon":
        return (
          <>
            <path d={circle} fill="currentColor" />
            <path d="M8.3 12.2l2.6 2.6 4.9-5.2" fill="none" stroke="var(--ink)" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
          </>
        );
      case "denied":
        return (
          <>
            <path d={seal} fill="currentColor" />
            <path d="M9.2 9.2l5.6 5.6M14.8 9.2l-5.6 5.6" stroke="var(--ink)" strokeWidth="1.9" strokeLinecap="round" />
          </>
        );
      case "raised":
        return (
          <>
            <path d={circle} fill="currentColor" />
            <path d="M12 16.5v-9M8.3 11l3.7-3.7 3.7 3.7" fill="none" stroke="var(--ink)" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
          </>
        );
      case "withdrawn":
        return (
          <>
            <path d={circle} fill="currentColor" />
            <path d="M9.5 9.5H14a2.8 2.8 0 0 1 0 5.6h-3.5M9.5 9.5l2.2-2.2M9.5 9.5l2.2 2.2" fill="none" stroke="var(--ink)" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
          </>
        );
    }
  })();
  return (
    <svg className={`result-icon tint-${tintOf(result)}`} viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false">
      {body}
    </svg>
  );
}

/** The app's trophy hero mark (LogOutcomeView.swift:99-104, `trophy.fill` in a savings-tinted circle). */
export function TrophyMark() {
  return (
    <span className="trophy-mark" aria-hidden="true">
      <svg viewBox="0 0 24 24" width="28" height="28" focusable="false">
        <path d="M7 3h10v2h3v2.5a4.5 4.5 0 0 1-4.1 4.5A5 5 0 0 1 13 14.9V17h3v3H8v-3h3v-2.1A5 5 0 0 1 8.1 12 4.5 4.5 0 0 1 4 7.5V5h3zm0 4H6v.5A2.5 2.5 0 0 0 7.3 9.7 6 6 0 0 1 7 8zm10 0v1a6 6 0 0 1-.3 1.7A2.5 2.5 0 0 0 18 7.5V7z" fill="currentColor" />
      </svg>
    </span>
  );
}

/** A logged outcome's title (WEB-COPY-1 item 4), else the app's status badge: "Generated" once a letter has shipped,
 *  "Draft" before (WEB-PARITY-1B, HM-1). The website records no export, so "Exported" is never shown. */
export function badgeWords(appeal: AppealSnapshot): AnyHeldId {
  if (appeal.outcome !== null) return `outcomes.labels.${appeal.outcome.result}.title` as AnyHeldId;
  return appeal.letter.status === "shipped" ? "status.generated" : "status.draft";
}

export function Badge({ appeal }: { appeal: AppealSnapshot }) {
  const words = badgeWords(appeal);
  const tone = appeal.outcome !== null ? `tint-${tintOf(appeal.outcome.result)}` : appeal.letter.status === "shipped" ? "generated" : "draft";
  return (
    <span className={`badge ${tone}`} data-appeal-status={appeal.status} data-held-copy={words}>
      {appeal.outcome !== null && <ResultIcon result={appeal.outcome.result} />}
      {heldText(words)}
    </span>
  );
}

/** ONE PROPERTY ON `/home`: its appeals, newest first, as the app's `Home` holds them. */
export interface PropertyGroup {
  readonly key: string;
  /** Newest first, each with the server's summary beside its snapshot. */
  readonly appeals: readonly { readonly snapshot: AppealSnapshot; readonly summary: AppealSummary | null }[];
}

/**
 * THE SERVER'S LIST, GROUPED INTO PROPERTIES BY THE SERVER'S OWN KEY (`AppealSummary.propertyKey`, the port of
 * `Home.normalizedKey`). Order is the list's — newest appeal first — so a property sits where its newest appeal does, and
 * its own appeals keep that order (`Home.sortedAppeals`). Nothing is decided here beyond "same key, same card"; an
 * appeal the server sent no summary for stands alone.
 *
 * ONLY AN APPEAL A CREDIT WAS SPENT ON IS A PROPERTY (WALK-FIX-3, R240). Tyler's third walk (2026-10-06)
 * found "Untitled property · DRAFT · Finish your draft" on `/home`. That row is an address the owner typed and never
 * paid for: `v2ResolveProperty` `address` mints the appeal with `subject: null` (resolve/address.ts, "the subject is
 * W2.4d's"), and only the record phase, after the spend, fills it — so on the server every unpaid start has no address
 * to show. The app never lists one: its Properties tab shows `store.homes` (PropertiesTab.swift:31), and the entry flow
 * saves a draft there only "the instant a credit is applied" (`persistResumableDraft`, AppealEntryFlow.swift:1101 and
 * :1299-1312), with the address the owner confirmed. So here too: an appeal is listed once the server's own
 * `entitlement.unlocked` says its credit was spent, and never before. A PAID appeal is never hidden, address or not — a
 * spent credit always has its row, under the app's own "Untitled property" if its record never arrived. The server's
 * `list` keeps returning every appeal, unpaid ones included: it is also the snapshot read (`../app/snapshot.ts`), and
 * the look-around's sign-in (`useUnpaidLookAround`) must still find an unpaid appeal there.
 */
export function groupProperties(appeals: readonly AppealSnapshot[], summaries: readonly AppealSummary[] | undefined): PropertyGroup[] {
  const byId = new Map((summaries ?? []).map((s) => [s.appealId, s]));
  const groups = new Map<string, { snapshot: AppealSnapshot; summary: AppealSummary | null }[]>();
  for (const snapshot of appeals) {
    if (!snapshot.entitlement.unlocked) continue;
    const summary = byId.get(snapshot.appealId) ?? null;
    const key = summary?.propertyKey ?? `appeal:${snapshot.appealId}`;
    groups.set(key, [...(groups.get(key) ?? []), { snapshot, summary }]);
  }
  return [...groups.entries()].map(([key, list]) => ({ key, appeals: list }));
}

/** `Home.title` (Models.swift:2854-2857): the address, else "Untitled property". */
export function propertyTitle(group: PropertyGroup): string {
  const address = group.appeals.map((a) => a.snapshot.subject?.address ?? "").find((a) => a.trim() !== "") ?? "";
  return address.trim() === "" ? afterText("home.untitled") : address.trim();
}

/** Where an appeal reopens (HomeDetailView.swift:79-80 reopens the flow): its letter once one has shipped; the Letter
 *  step while the appeal is still waiting on one (WEB-RESUME, R242: the server's head stays `queued` until
 *  the first read of the job's ending, and the Letter step reads it back — the overlay while it is being written, the
 *  letter once it has shipped); else step 1. */
export function reopenPath(appeal: AppealSnapshot): string {
  if (appeal.letter.status === "shipped" && appeal.letter.latestGenerationAt !== null) {
    return `/letter?generationAt=${appeal.letter.latestGenerationAt}`;
  }
  if ((appeal.letter.status === "queued" || appeal.letter.status === "generating") && appeal.letter.latestGenerationAt !== null) {
    return "/you";
  }
  return "/details";
}
